import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, Select, Space, Typography, message } from 'antd';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import { useAuth } from '../context/AuthContext';

const { Title, Text } = Typography;

const SubscriptionCheckoutPlaceholder: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [submitting, setSubmitting] = useState(false);
    const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>(
        (searchParams.get('cycle') === 'annual' ? 'annual' : 'monthly')
    );

    const planCode = (searchParams.get('plan') || 'solo').trim().toLowerCase();
    const flow = (searchParams.get('flow') || '').trim().toLowerCase();
    const companyId = Number(searchParams.get('companyId') || 0);
    const successUrl = (searchParams.get('successUrl') || '/login?subscription=success').trim();
    const cancelUrl = (searchParams.get('cancelUrl') || '/login?subscription=cancel').trim();
    const isLoggedInUpgrade = Boolean(currentUser && companyId > 0);
    const backPath = cancelUrl || (currentUser ? '/select-company' : '/login');

    const title = useMemo(() => {
        if (flow === 'register') {
            return 'Payment Required';
        }
        return 'Upgrade Subscription';
    }, [flow]);

    const handleSimulatePaymentSuccess = async () => {
        if (!isLoggedInUpgrade) {
            navigate('/login');
            return;
        }

        setSubmitting(true);
        try {
            await axios.post(
                ENDPOINTS.UPGRADE_OWN_COMPANY_PLAN(companyId),
                {
                    planCode,
                    billingCycle,
                },
                { withCredentials: true }
            );
            message.success('Subscription upgraded successfully.');
            navigate(successUrl);
        } catch (error: any) {
            message.error(error?.response?.data?.message || error?.response?.data?.error || 'Failed to upgrade subscription.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <Card style={{ width: '100%', maxWidth: 560 }}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Title level={3} style={{ marginBottom: 0 }}>{title}</Title>
                    <Alert
                        type="warning"
                        showIcon
                        message="Checkout placeholder"
                        description="Stripe/checkout integration is not connected yet. This page is a temporary placeholder."
                    />
                    <Text>
                        Selected plan: <strong>{planCode.toUpperCase()}</strong>
                    </Text>

                    {isLoggedInUpgrade && (
                        <>
                            <Text>Billing cycle</Text>
                            <Select
                                value={billingCycle}
                                onChange={(value: 'monthly' | 'annual') => setBillingCycle(value)}
                                style={{ width: 220 }}
                                options={[
                                    { value: 'monthly', label: 'Monthly' },
                                    { value: 'annual', label: 'Annual' },
                                ]}
                            />
                        </>
                    )}

                    <Space wrap>
                        {isLoggedInUpgrade ? (
                            <Button type="primary" loading={submitting} onClick={handleSimulatePaymentSuccess}>
                                Simulate payment success
                            </Button>
                        ) : (
                            <Button type="primary" onClick={() => navigate(successUrl)}>
                                Back to login
                            </Button>
                        )}
                        <Button onClick={() => navigate(backPath)}>Back</Button>
                    </Space>
                </Space>
            </Card>
        </div>
    );
};

export default SubscriptionCheckoutPlaceholder;
